import type { ReactNode } from 'react'
import type {
  AiAgentContinueResponse,
  AiChatMessage,
  AiCompletionSummary,
  AiContextUsedReceipt,
} from '../../../../data/ServerApiClient'
import { formatCurrencyCompact, trimStr } from '../cfoFormat'
import type { CfoAnalysisAction, CfoAnalysisChartSpec } from './types'
import './CfoAnalysisStream.css'

// The guided-analysis stream renderer shared by the analysis modal (and the
// Home Brief lineage it was lifted from): an AI message stream with inline,
// clickable charts/tables, next-step action chips, the final structured answer,
// and pending/error states. Deterministic evidence renders first (progress
// cards), the model narrates after — one renderer, used everywhere.

// ---------------------------------------------------------------------------
// Inline chart / table card
// ---------------------------------------------------------------------------

export function CfoAnalysisChart({
  chart,
  onPrompt,
  disabled = false,
}: {
  chart: CfoAnalysisChartSpec
  onPrompt: (prompt: string) => void
  disabled?: boolean
}) {
  if (chart.kind === 'table') {
    const columns =
      Array.isArray(chart.columns) && chart.columns.length
        ? chart.columns
        : [
            { key: 'label', label: 'Item' },
            { key: 'value', label: 'Value', align: 'right' as const },
          ]
    const rows = Array.isArray(chart.rows) ? chart.rows.slice(0, 8) : []
    return (
      <div className="cfo-analysis-chart table">
        <div className="cfo-analysis-chart-head">
          <b>{chart.title}</b>
          {chart.subtitle ? <span>{chart.subtitle}</span> : null}
        </div>
        <div className="cfo-analysis-table-wrap">
          <table className="cfo-analysis-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} className={column.align === 'right' ? 'is-right' : ''}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx}>
                  {columns.map((column) => {
                    const raw = row?.[column.key]
                    const value =
                      typeof raw === 'number'
                        ? formatCurrencyCompact(Math.abs(raw)).replace('$', raw < 0 ? '-$' : '$')
                        : trimStr(raw) || '—'
                    return (
                      <td key={column.key} className={column.align === 'right' ? 'is-right' : ''}>
                        {value}
                      </td>
                    )
                  })}
                </tr>
              ))}
              {!rows.length ? (
                <tr>
                  <td colSpan={columns.length}>No rows cleared the display threshold.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    )
  }
  const items = Array.isArray(chart.items) ? chart.items.filter((item) => Number.isFinite(Number(item.value))) : []
  const maxAbs = Math.max(1, ...items.map((item) => Math.abs(Number(item.value) || 0)))
  return (
    <div className={`cfo-analysis-chart ${chart.kind}`}>
      <div className="cfo-analysis-chart-head">
        <b>{chart.title}</b>
        {chart.subtitle ? <span>{chart.subtitle}</span> : null}
      </div>
      <div className="cfo-analysis-chart-rows">
        {items.map((item, idx) => {
          const value = Number(item.value) || 0
          const width = Math.max(6, Math.round((Math.abs(value) / maxAbs) * 100))
          const direction = value > 0 ? 'up' : value < 0 ? 'down' : 'neutral'
          const prompt = trimStr((item as any).prompt)
          const rowContent = (
            <>
              <div className="cfo-analysis-chart-label">
                <span>{item.label}</span>
                {item.note ? <small>{item.note}</small> : null}
              </div>
              <div className="cfo-analysis-chart-track" aria-hidden="true">
                <div className={`cfo-analysis-chart-bar ${direction}`} style={{ width: `${width}%` }} />
              </div>
              <div className={`cfo-analysis-chart-value ${direction}`}>
                {value > 0 ? '+' : value < 0 ? '-' : ''}
                {formatCurrencyCompact(Math.abs(value))}
              </div>
            </>
          )
          return prompt ? (
            <button
              type="button"
              className="cfo-analysis-chart-row is-clickable"
              key={`${item.label}-${idx}`}
              disabled={disabled}
              onClick={() => onPrompt(prompt)}
              title="Ask the analyst to drill into this item"
            >
              {rowContent}
            </button>
          ) : (
            <div className="cfo-analysis-chart-row" key={`${item.label}-${idx}`}>
              {rowContent}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// One message (text + optional chart + next-step actions)
// ---------------------------------------------------------------------------

export function CfoAnalysisMessage({
  message,
  onPrompt,
  disabled = false,
  tagButton,
}: {
  message: AiChatMessage
  onPrompt: (prompt: string) => void
  disabled?: boolean
  /** Optional "Tag to report" affordance rendered under a chart card. */
  tagButton?: ReactNode
}) {
  const chart = message.metadata?.chart
  const actions: CfoAnalysisAction[] = Array.isArray(message.metadata?.actions)
    ? message.metadata!.actions
        .map((action: any) => ({ label: trimStr(action?.label), prompt: trimStr(action?.prompt) }))
        .filter((action: CfoAnalysisAction) => action.label && action.prompt)
        .slice(0, 4)
    : []
  const actionRow = actions.length ? (
    <div className="cfo-analysis-actions">
      {actions.map((action) => (
        <button type="button" key={action.prompt} disabled={disabled} onClick={() => onPrompt(action.prompt)}>
          {action.label}
        </button>
      ))}
    </div>
  ) : null

  if (!chart) {
    return (
      <>
        {message.content}
        {actionRow}
      </>
    )
  }
  return (
    <div className="cfo-analysis-chart-card">
      {message.content ? <div className="cfo-analysis-chart-kicker">{message.content}</div> : null}
      <CfoAnalysisChart chart={chart} onPrompt={onPrompt} disabled={disabled} />
      {tagButton ? <div className="cfo-analysis-card-tag">{tagButton}</div> : null}
      {actionRow}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Full stream
// ---------------------------------------------------------------------------

const receiptItems = (items: unknown): string[] =>
  Array.isArray(items) ? items.map(trimStr).filter(Boolean) : []

const receiptLabel = (value: unknown): string =>
  trimStr(value)
    .replace(/[_:]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())

const receiptDate = (value: unknown): string => {
  const text = trimStr(value)
  if (!text) return '—'
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : text
}

function CompletionList({ title, items, tone = 'default' }: { title: string; items: string[]; tone?: 'default' | 'warning' }) {
  if (!items.length) return null
  return (
    <section className={`cfo-analysis-completion-section ${tone === 'warning' ? 'is-warning' : ''}`}>
      <h4>{title}</h4>
      <ul>
        {items.map((item, index) => <li key={`${title}-${index}`}>{item}</li>)}
      </ul>
    </section>
  )
}

function StructuredCompletion({ summary }: { summary: AiCompletionSummary }) {
  const found = receiptItems(summary.what_i_found)
  const changed = receiptItems(summary.what_i_changed)
  const matters = receiptItems(summary.what_matters)
  const verification = receiptItems(summary.verification_performed)
  const exceptions = receiptItems(summary.exceptions_and_unresolved_items)
  return (
    <article className="cfo-analysis-completion" aria-label="Validated financial analysis">
      <header>
        <span className={`cfo-analysis-completion-status ${summary.financially_verified ? 'is-verified' : ''}`}>
          {summary.financially_verified ? 'Verified' : exceptions.length ? 'Completed with exceptions' : 'Analysis completed'}
        </span>
        <h3>{trimStr(summary.headline) || 'Financial analysis completed'}</h3>
      </header>
      <CompletionList title="What I found" items={found} />
      <CompletionList title="What matters" items={matters} />
      <CompletionList title="What I changed" items={changed} />
      <CompletionList title="Exceptions and unresolved items" items={exceptions} tone="warning" />
      {verification.length ? (
        <details className="cfo-analysis-verification">
          <summary>Verification performed</summary>
          <ul>{verification.map((item, index) => <li key={`verification-${index}`}>{item}</li>)}</ul>
        </details>
      ) : null}
    </article>
  )
}

function ContextUsed({ receipt }: { receipt: AiContextUsedReceipt }) {
  const metrics = receiptItems(receipt.relevant_metric_ids)
  const statements = receiptItems(receipt.relevant_statement_ids)
  const conflicts = receiptItems(receipt.context_conflicts)
  return (
    <details className="cfo-analysis-context-used">
      <summary>
        <span>Context used</span>
        <small>{trimStr(receipt.company_name)} · through {receiptDate(receipt.effective_through)}</small>
      </summary>
      <dl>
        <div><dt>Actuals available</dt><dd>{receiptDate(receipt.latest_available_period || receipt.effective_through)}</dd></div>
        <div><dt>Reconciled through</dt><dd>{receiptDate(receipt.latest_reconciled_period)}</dd></div>
        <div><dt>Source health</dt><dd>{receiptLabel(receipt.source_health) || '—'}</dd></div>
        <div><dt>Evidence used</dt><dd>{Number(receipt.evidence_ref_count) || 0} references</dd></div>
      </dl>
      {statements.length ? <p><strong>Statements:</strong> {statements.map(receiptLabel).join(', ')}</p> : null}
      {metrics.length ? <p><strong>Relevant metrics:</strong> {metrics.map(receiptLabel).join(', ')}</p> : null}
      {receipt.plan_publication_id ? <p><strong>Plan publication:</strong> {receipt.plan_publication_id}</p> : null}
      {receipt.financial_period_disclosure ? <p className="cfo-analysis-context-disclosure">{receipt.financial_period_disclosure}</p> : null}
      {conflicts.length ? (
        <div className="cfo-analysis-context-conflicts">
          <strong>Context conflicts requiring review</strong>
          <ul>{conflicts.map((item, index) => <li key={`context-conflict-${index}`}>{item}</li>)}</ul>
        </div>
      ) : null}
    </details>
  )
}

export type CfoAnalysisStreamProps = {
  messages: AiChatMessage[]
  response: AiAgentContinueResponse | null
  busy: boolean
  error: string
  promptChips: Array<{ label: string; prompt: string }>
  onPrompt: (prompt: string) => void
  /** Opening copy shown before any turn (evidence card precedes this). */
  intro?: ReactNode
  /** Rendered below the final answer (feedback, tag-to-report). */
  answerFooter?: ReactNode
  /** Optional per-chart "Tag to report" builder. */
  renderChartTag?: (message: AiChatMessage, index: number) => ReactNode
}

export function CfoAnalysisStream({
  messages,
  response,
  busy,
  error,
  promptChips,
  onPrompt,
  intro,
  answerFooter,
  renderChartTag,
}: CfoAnalysisStreamProps) {
  const answer = response?.answer
  const completion = response?.financial_work_receipt?.completion?.completion_summary || null
  const contextUsed = response?.financial_work_receipt?.context_used || null
  const answerDetail = answer
    ? {
        headline: trimStr(answer.headline),
        summary: trimStr(answer.executive_summary),
        narrative: (answer.detailed_narrative || []).map(trimStr).filter(Boolean),
        caveats: (answer.caveats || []).map(trimStr).filter(Boolean),
      }
    : null

  return (
    <div className="cfo-analysis-stream">
      {messages.length === 0 && intro ? <div className="cfo-analysis-msg ai warm">{intro}</div> : null}
      {messages.map((msg, idx) => (
        <div className={`cfo-analysis-msg ${msg.role === 'user' ? 'user' : 'ai'}`} key={`${msg.role}-${idx}`}>
          <CfoAnalysisMessage
            message={msg}
            onPrompt={onPrompt}
            disabled={busy}
            tagButton={renderChartTag ? renderChartTag(msg, idx) : null}
          />
        </div>
      ))}
      {busy ? <div className="cfo-analysis-msg ai pending">Thinking…</div> : null}
      {error ? <div className="cfo-analysis-msg ai error">{error}</div> : null}
      {completion ? <StructuredCompletion summary={completion} /> : answerDetail && (answerDetail.headline || answerDetail.summary || answerDetail.narrative.length || answerDetail.caveats.length) ? (
        <div className="cfo-analysis-msg ai detail">
          {answerDetail.headline ? <p className="cfo-analysis-answer-headline"><strong>{answerDetail.headline}</strong></p> : null}
          {answerDetail.summary ? <p>{answerDetail.summary}</p> : null}
          {answerDetail.narrative.map((item, idx) => (
            <p key={`n-${idx}`}>{item}</p>
          ))}
          {answerDetail.caveats.map((item, idx) => (
            <p className="caveat" key={`c-${idx}`}>
              Caveat: {item}
            </p>
          ))}
        </div>
      ) : null}
      {contextUsed ? <ContextUsed receipt={contextUsed} /> : null}
      {answerFooter}
      {promptChips.length ? (
        <div className="cfo-analysis-prompts">
          {promptChips.map((chip) => (
            <button type="button" className="cfo-analysis-chip" key={chip.prompt} disabled={busy} onClick={() => onPrompt(chip.prompt)}>
              {chip.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default CfoAnalysisStream
